import {
  Document,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useMemo } from "react";
import {
  formatDate,
  formatDateNumbers,
} from "../../../components/lists/formatDate";
import { Metadata } from "../../../types/Metadata";
import {
  Answer,
  ParticipantAnswer,
  ProblemAnswer,
  ProblemType,
  RaportSection,
  ReportType,
  TemplateType,
} from "../../../types/Rows";
import ScientiaHeader from "../../../components/ScientiaHeader";
import { isMultiDayVisit } from "../../../utils/visitDate";
import { ActionStatus, ReportActionDto } from "../../../types/reportActions";
import "./pdfFonts";

/* =================================================================== */
/* Fonts & Hyphenation (wrap long tokens)                              */
/* =================================================================== */

// rejestracja Roboto i dzielenia słów: ./pdfFonts (wspólne z ReportDocumentV2)

/* =================================================================== */
/* Design tokens & Consolidated Styles                                 */
/* =================================================================== */

const TOKENS = {
  color: {
    text: "#000000",
    muted: "gray",
    tableHeaderBg: "#f5f5f5",
    tableHeaderBorder: "#bdbdbd",
    tableRowBorder: "#e0e0e0",
    black: "#000000",
  },
  border: {
    thin: 1,
  },
  space: {
    xxs: 1,
    xs: 2,
    sm: 4,
    md: 8,
    lg: 10,
    xl: 16,
  },
  font: {
    xs: 9,
    sm: 10,
    base: 12,
    lg: 14,
    xl: 20,
  },
};

const styles = StyleSheet.create({
  /* ---------- Page / Typography ---------- */
  page: {
    fontFamily: "Roboto",
    fontSize: TOKENS.font.base,
    paddingBottom: 65,
    paddingHorizontal: 35,
    backgroundColor: "white",
    color: TOKENS.color.text,
  },
  title: {
    fontSize: TOKENS.font.xl,
    fontWeight: "semibold",
    marginBottom: TOKENS.space.lg,
    textAlign: "center",
  },
  section: {
    marginTop: TOKENS.space.sm,
    marginBottom: TOKENS.space.sm,
    fontSize: TOKENS.font.sm,
  },
  sectionTitle: {
    fontSize: TOKENS.font.base,
    fontWeight: "semibold",
  },
  importantParagraph: {
    fontSize: TOKENS.font.lg,
    fontWeight: "bold",
    marginTop: TOKENS.space.lg,
  },
  muted: { color: TOKENS.color.muted },
  textXs: { fontSize: TOKENS.font.xs },
  textSm: { fontSize: TOKENS.font.sm },

  pageNumber: {
    position: "absolute",
    fontSize: TOKENS.font.base,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: TOKENS.color.muted,
  },

  /* ---------- Layout utils ---------- */
  row: { display: "flex", flexDirection: "row" },
  rowBetween: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  wrap: { flexWrap: "wrap" },
  flex1: { flex: 1 },
  maxW94: { maxWidth: "94%" },

  /* spacing */
  p2: { padding: TOKENS.space.xs },
  p4: { padding: TOKENS.space.sm },
  pt5: { paddingTop: TOKENS.space.lg },
  gapSm: { gap: TOKENS.space.sm },
  gapMd: { gap: TOKENS.space.md },
  gapLg: { gap: TOKENS.space.lg },
  pl2: { paddingLeft: TOKENS.space.xs },
  mr2: { marginRight: TOKENS.space.xs },

  /* borders */
  bTop: { borderTop: TOKENS.border.thin, borderStyle: "solid" },
  bBottom: { borderBottom: TOKENS.border.thin, borderStyle: "solid" },
  bLeft: { borderLeft: TOKENS.border.thin, borderStyle: "solid" },
  bRight: { borderRight: TOKENS.border.thin, borderStyle: "solid" },
  bGrayLight: { borderColor: TOKENS.color.tableRowBorder },
  bGray: { borderColor: TOKENS.color.tableHeaderBorder },
  bBlack: { borderColor: TOKENS.color.black },

  separator: {
    borderBottom: TOKENS.border.thin,
    borderColor: TOKENS.color.tableRowBorder,
    paddingTop: TOKENS.space.lg,
  },

  /* ---------- Generic table styles ---------- */
  table: { marginTop: TOKENS.space.md, fontSize: TOKENS.font.xs },
  thRow: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: TOKENS.color.tableHeaderBg,
    borderTop: TOKENS.border.thin,
    borderBottom: TOKENS.border.thin,
    borderColor: TOKENS.color.tableHeaderBorder,
  },
  tr: {
    display: "flex",
    flexDirection: "row",
    borderBottom: TOKENS.border.thin,
    borderColor: TOKENS.color.tableRowBorder,
  },
  cell: { padding: TOKENS.space.sm },

  /* column width helpers */
  w5: { width: "5%" },
  w9: { width: "9%" },
  w10: { width: "10%" },
  w11: { width: "11%" },
  w12: { width: "12%" },
  w15: { width: "15%" },
  w20: { width: "20%" },
  w22: { width: "22%" },
  w23: { width: "23%" },
  w30: { width: "30%" },
  w35: { width: "35%" },
  w40: { width: "40%" },
  w55: { width: "55%" },
  w25: { width: "25%" },

  /* ---------- Specific blocks ---------- */
  actionsTable: { marginTop: TOKENS.space.md, fontSize: TOKENS.font.xs },
  actionsTitle: {
    fontSize: TOKENS.font.lg,
    fontWeight: "bold",
    marginBottom: TOKENS.space.md,
  },

  flexWrap: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: TOKENS.space.sm,
  },
  columsSection: {
    margin: TOKENS.space.sm,
    flexGrow: 1,
    fontSize: TOKENS.font.sm,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
  },

  borderedItem: {
    borderBottom: TOKENS.border.thin,
    borderColor: TOKENS.color.tableRowBorder,
    borderStyle: "solid",
    minWidth: "25%",
  },
  borderedItemSolid: {
    padding: TOKENS.space.xs,
    borderBottom: TOKENS.border.thin,
    borderColor: TOKENS.color.black,
    borderStyle: "solid",
  },
  nonBorderedItem: { minWidth: "25%" },
});

/* =================================================================== */
/* Helpers                                                             */
/* =================================================================== */

const statusName = (val?: string) => {
  if (val === "new") return "Nowe";
  else if (val === "ongoing") return "Trwające";
  if (val === "finished") return "Zakończone";
  else return "Brak statusu";
};

const getRadioResult = (isNoAnswer: boolean, val?: string) => {
  if (isNoAnswer) return "";
  if (val === "yes") return "TAK";
  else if (val === "no") return "NIE";
  else if (val === "na") return "NA";
  else return " ";
};

const formatIso = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

const statusPl = (s: ActionStatus) =>
  s === ActionStatus.Closed ? "Zamknięte" : "Otwarte";

const boolPl = (v: boolean) => (v ? "TAK" : "NIE");

const capitalizeWords = (str: string) =>
  str
    .split(" ")
    .map((word) =>
      word
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join("-")
    )
    .join(" ");

const getBoolTextVal = (val: boolean) => (val ? "TAK" : "NIE");

const groupByToMap = <T, Q>(
  array: T[],
  predicate: (value: T, index: number, array: T[]) => Q
) =>
  array.reduce((map, value, index, arr) => {
    const key = predicate(value, index, arr);
    map.get(key)?.push(value) ?? map.set(key, [value]);
    return map;
  }, new Map<Q, T[]>());

/* =================================================================== */
/* Report Actions Table                                                */
/* =================================================================== */

// Rejestr akcji w formacie klienta (SR-014): 13 kolumn nie mieści się czytelnie na A4 w pionie,
// więc każda pozycja ma dwa wiersze: dane krótkie u góry, opis / działania / osoba odpowiedzialna pod spodem.
// Szerokości dobrane tak, żeby typowe pojedyncze słowa („Randomizacja”, „Zamknięte”) mieściły się
// bez nachodzenia (PDF łamie dopiero słowa dłuższe niż 18 znaków)
const ACTION_META = [
  { label: "lp.", width: "4%" },
  { label: "Nr uczestnika", width: "12%" },
  { label: "Numer wizyty", width: "13%" },
  { label: "Czego dotyczy", width: "15%" },
  { label: "Data identyfikacji", width: "10%" },
  { label: "Data zamknięcia", width: "10%" },
  { label: "Status", width: "10%" },
  { label: "PD (pot.)", width: "8%" },
  { label: "Poważne znal. (pot.)", width: "9%" },
  { label: "Serious Breach", width: "9%" },
];
const ACTION_TEXT = [
  { label: "Opis", width: "40%" },
  { label: "Podjęte działania / Follow Up", width: "40%" },
  { label: "Osoba odpowiedzialna w ośrodku", width: "20%" },
];

const ReportActionsTable = ({ items, categories = [] }: { items: ReportActionDto[]; categories?: { id: number; value: string }[] }) => {
  // ✅ Hooks ALWAYS at the top (before any return)
  const sorted = useMemo(
    () =>
      (items ?? [])
        .slice()
        .sort((a, b) =>
          (a.identificationDate || "").localeCompare(b.identificationDate || "")
        ),
    [items]
  );

  if(items.length==0)
    return null;

  const categoryName = (id?: number | null) => categories.find((c) => c.id === id)?.value?.trim() ?? "";
  const headerCells = (cols: { label: string; width: string }[]) =>
    cols.map((c) => (
      <View key={c.label} style={[styles.cell, { width: c.width }]}>
        <Text>{c.label}</Text>
      </View>
    ));

  return (
    <View style={styles.actionsTable}>
      <Text style={styles.actionsTitle}>Akcje otwarte i zamknięte</Text>

      {/* Header: dwa wiersze, jak pozycje */}
      <View wrap={false} fixed={false}>
        <View style={[styles.thRow, { borderBottom: 0 }]}>{headerCells(ACTION_META)}</View>
        <View style={[styles.thRow, { borderTop: 0, color: TOKENS.color.muted }]}>{headerCells(ACTION_TEXT)}</View>
      </View>

      {sorted.map((ra, idx) => {
        const meta = [
          String(idx + 1),
          ra.participantNumber || "",
          ra.visitNumber || "",
          categoryName(ra.categoryId),
          formatIso(ra.identificationDate),
          formatIso(ra.closedDate),
          statusPl(ra.status),
          boolPl(!!ra.potentialDeviation),
          boolPl(!!ra.potentialSeriousFinding),
          boolPl(!!ra.seriousBreach),
        ];
        const texts = [ra.deviationDescription || "", ra.actionsTaken || "", ra.responsiblePerson || ""];
        return (
          <View key={ra.id || idx} wrap={false} style={{ borderBottom: TOKENS.border.thin, borderColor: TOKENS.color.tableRowBorder }}>
            <View style={{ display: "flex", flexDirection: "row" }}>
              {meta.map((v, i) => (
                <View key={i} style={[styles.cell, { width: ACTION_META[i].width, paddingBottom: TOKENS.space.xxs }]}>
                  <Text style={i >= 7 && v === "TAK" ? { fontWeight: "bold" } : undefined}>{v}</Text>
                </View>
              ))}
            </View>
            <View style={{ display: "flex", flexDirection: "row" }}>
              {texts.map((v, i) => (
                <View key={i} style={[styles.cell, { width: ACTION_TEXT[i].width, paddingTop: TOKENS.space.xxs }]}>
                  <Text>{v}</Text>
                </View>
              ))}
            </View>
          </View>
        );
      })}
    </View>
  );
};

/* =================================================================== */
/* Main Component                                                      */
/* =================================================================== */

export const DocumentGenerator = ({
  reportName,
  resortName,
  address,
  metadata,
  problemAnswers,
  sections,
  participants,
  types,
  footer,
  author,
  composedDate,
  actions,
  reportType = ReportType.DEFAULT,
  visitLabel,
  cmResorts = [],
  categories = [],
}: {
  reportName: string;
  address: string;
  resortName: string;
  metadata: Metadata;
  problemAnswers: ProblemAnswer[];
  sections: RaportSection[];
  participants: ParticipantAnswer[];
  actions: ReportActionDto[];
  types: TemplateType[];
  footer: string;
  author: string;
  composedDate: Date;
  reportType?: ReportType;
  // Nazwa raportu z wizyty ("IMV 5"): typ i numer wizyty z nagłówka. CM: brak (stare pole z metadanych)
  visitLabel?: string;
  cmResorts?: string[];
  // nazwy kategorii do kolumny „Czego dotyczy” w rejestrze akcji
  categories?: { id: number; value: string }[];
}) => {
  // Liczone przy renderze, nie w useEffect: eksport PDF (pdf().toBlob()) robi layout z pierwszego renderu,
  // więc pytania podrzędne (warunkowe) z efektu mogły nie trafić do pliku.
  const questionsCopy = useMemo(() => {
    const qc = new Map<string, Answer>();
    sections
      .flatMap((x) => x.questionsWithAnswer)
      .forEach((q) => qc.set(q.questionId, q));

    sections
      .flatMap((x) => x.questionsWithAnswer)
      .sort((x) => (x.parentId == null ? -1 : 1))
      .forEach((element) => {
        if (element.parentId && qc.has(element.parentId)) {
          const val = qc.get(element.parentId)!;
          val.children = val.children
            ? [
                ...val.children.filter(
                  (x) => x.questionId !== element.questionId
                ),
                element,
              ]
            : [element];
        }
      });

    return qc;
  }, [sections]);

  const isChildren = (item: Answer) =>
    questionsCopy.has(item.parentId) || item.requiredAnswerToBeVisible != null;

  const getVisitDate = (m: Metadata) =>
    isMultiDayVisit(m.visitDate, m.visitDateEnd)
      ? `od ${formatDate(m.visitDate)} do ${formatDate(m.visitDateEnd)}`
      : formatDate(m.visitDate);

  const QuestionChildren = ({
    answer,
    index,
    questionIndex,
    childIndex,
  }: {
    answer: Answer;
    index: number;
    questionIndex: number;
    childIndex?: number;
  }) => (
    <>
      {answer.children
        ?.filter(
          (x) =>
            answer.answer.toLowerCase() ===
            x?.requiredAnswerToBeVisible?.toLowerCase()
        )
        ?.sort((a, b) => (new Date(a.created) > new Date(b.created) ? 1 : -1))
        .map((childQ, childQIndex) => (
          <View key={childQ.questionId}>
            <View style={[styles.rowBetween, styles.bTop, styles.bLeft, styles.bGray]}>
              <Text style={[styles.maxW94, styles.pl2]}>
                {index + 1}.{questionIndex + 1}.
                {childIndex ? childIndex + "." : ""}
                {childQIndex + 1}. {childQ.description}
              </Text>
              <View style={[styles.bLeft, styles.bRight, styles.bGray, styles.w5]}>
                <Text style={[styles.pl2, styles.mr2]}>
                  {getRadioResult(childQ.isBlank, childQ.answer ?? "")}
                </Text>
              </View>
            </View>
            <QuestionChildren
              answer={childQ}
              index={index}
              questionIndex={questionIndex}
              childIndex={childQIndex + 1}
            />
          </View>
        ))}
    </>
  );

  const ChildrenComments = ({
    answer,
    index,
    questionIndex,
    childIndex,
  }: {
    answer: Answer;
    index: number;
    questionIndex: number;
    childIndex?: number;
  }) => (
    <View>
      {answer.children
        ?.filter(
          (x) =>
            answer.answer.toLowerCase() ===
            x?.requiredAnswerToBeVisible?.toLowerCase()
        )
        ?.sort((a, b) => (new Date(a.created) > new Date(b.created) ? 1 : -1))
        .map((childQ, childQIndex) => (
          <View key={childQ.questionId}>
            {!!childQ.comment && (
              <Text>
                {index + 1}.{questionIndex + 1}.
                {childIndex ? childIndex + "." : ""}
                {childQIndex + 1}. {childQ.comment}
              </Text>
            )}
            <ChildrenComments
              answer={childQ}
              index={index}
              questionIndex={questionIndex}
              childIndex={childQIndex + 1}
            />
          </View>
        ))}
    </View>
  );

  const QuestionsParagraph = () => {
    const isCM = reportType === ReportType.CENTRAL_MONITORING;
    const sortedSections = [...sections].sort((a, b) => (a.weight < b.weight ? -1 : 1));

    // Flat offset for CM numbering
    let cmOffset = 0;

    return (
      <View>
        {!isCM && <Text style={styles.importantParagraph}>PYTANIA</Text>}
        {sortedSections.map((section, index) => {
          const sectionOffset = cmOffset;
          if (isCM) {
            cmOffset += section.questionsWithAnswer.filter((x) => !isChildren(x)).length;
          }
          return (
          <View key={section.id || index} style={styles.section}>
            {!isCM && (
              <Text style={styles.sectionTitle}>
                {index + 1} {section.name}:
              </Text>
            )}

            <View style={[styles.bBottom, styles.bGray]}>
              {section.questionsWithAnswer
                .filter((x) => !isChildren(x))
                .sort((a, b) => (a.weight < b.weight ? -1 : 1))
                .map((q, questionIndex) => {
                  const qNum = isCM
                    ? `${sectionOffset + questionIndex + 1}.`
                    : `${index + 1}.${questionIndex + 1}.`;
                  return (
                  <View key={q.questionId}>
                    <View style={[styles.rowBetween, styles.bTop, styles.bLeft, styles.bGray]} wrap={false}>
                      <Text style={[styles.maxW94, styles.pl2]}>
                        {qNum} {q.description}
                        {q.isDirectAnswer && q.comment && (
                          <> {"\n- "} {q.comment}</>
                        )}
                      </Text>
                      <View style={[styles.bLeft, styles.bRight, styles.bGray, styles.w5]}>
                        <Text style={[styles.pl2, styles.mr2]}>
                          {getRadioResult(q.isBlank, q.answer ?? "")}
                        </Text>
                      </View>
                    </View>
                    <QuestionChildren
                      answer={q}
                      index={index}
                      questionIndex={questionIndex}
                    />
                    {isCM && !!q.comment && !q.isDirectAnswer && (
                      <Text style={[styles.textSm, styles.p2, styles.muted]}>
                        Komentarz: {q.comment}
                      </Text>
                    )}
                    {isCM && (
                      <ChildrenComments
                        answer={q}
                        index={index}
                        questionIndex={questionIndex}
                      />
                    )}
                  </View>
                  );
                })}
            </View>

            {!isCM && (
              <>
                <View>
                  <Text>Komentarze:</Text>
                </View>
                {section.questionsWithAnswer
                  .filter((x) => !isChildren(x))
                  .sort((a, b) => (a.weight < b.weight ? -1 : 1))
                  .map((q, questionIndex) => {
                    const qNum = `${index + 1}.${questionIndex + 1}.`;
                    return (
                      <View key={`${q.questionId}-comments`}>
                        {!!q.comment && !q.isDirectAnswer && (
                          <Text>
                            {qNum} {q.comment}
                          </Text>
                        )}
                        <ChildrenComments
                          answer={q}
                          index={index}
                          questionIndex={questionIndex}
                        />
                      </View>
                    );
                  })}
              </>
            )}
          </View>
          );
        })}
      </View>
    );
  };

  const MetaData = () => (
    <View>
      <View>
        <Text style={styles.importantParagraph}>Uczestnicy</Text>
        <View style={[styles.row, styles.wrap]}>
          <Text style={{ fontWeight: "bold" }}>CRO: </Text>
          <Text>{metadata.croParticipants}</Text>
        </View>
        <View style={[styles.row, styles.wrap]}>
          <Text style={{ fontWeight: "bold" }}>Ośrodek: </Text>
          <Text>{metadata.resortParticipants}</Text>
        </View>
      </View>

      <View style={styles.columsSection}>
        <Text style={styles.importantParagraph}>
          Status rekrutacji: {metadata.recrutationStatus}%
        </Text>
        <Text style={styles.importantParagraph}>
          Odchylenia od protokołu: {metadata?.protocolDeviation}
        </Text>
        <Text style={styles.importantParagraph}>
          SAE: {metadata?.reportedSAE}
        </Text>
      </View>

      <View style={styles.flexWrap}>
        {metadata.declaredParticipants != null && (
          <Text style={styles.nonBorderedItem}>
            Zadeklarowani: {metadata?.declaredParticipants}
          </Text>
        )}
        {metadata.duringTreatment != null && (
          <Text style={styles.nonBorderedItem}>
            W trakcie leczenia: {metadata?.duringTreatment}
          </Text>
        )}
        {metadata.afterScreening != null && (
          <Text style={styles.nonBorderedItem}>
            Skriningowani: {metadata?.afterScreening}
          </Text>
        )}
        {metadata?.randomized != null && (
          <Text style={styles.nonBorderedItem}>
            Zrandomizowani: {metadata?.randomized}
          </Text>
        )}
        {metadata?.prematureCompletion != null && (
          <Text style={styles.nonBorderedItem}>
            Kończący przedwcześnie EOT : {metadata?.prematureCompletion}
          </Text>
        )}
        {metadata?.screenFailure != null && (
          <Text style={styles.nonBorderedItem}>
            Z niepowodzeniem skriningu: {metadata?.screenFailure}
          </Text>
        )}
        {metadata?.duringFollowUp != null && (
          <Text style={styles.nonBorderedItem}>
            W trakcie obserwacji: {metadata?.duringFollowUp}
          </Text>
        )}
        {metadata?.completedStudy != null && (
          <Text style={styles.nonBorderedItem}>
            Zakończony udział EOS : {metadata?.completedStudy}
          </Text>
        )}
      </View>
    </View>
  );

  const Issues = ({
    problemType,
    text,
  }: {
    problemType: ProblemType;
    text: string;
  }) => {
    // ✅ Hooks BEFORE any return
    const items = problemAnswers.filter((x) => x.problemType === problemType);

    const grouped = useMemo(
      () => Array.from(groupByToMap(items, (x) => x.answer)),
      [items]
    );

    let counter = 0;

    if (grouped.length === 0) return null;

    return (
      <View>
        <Text style={styles.importantParagraph}>{text}</Text>
        {grouped.map((group, groupIndex) => (
          <View key={`g-${groupIndex}`} style={{ marginBottom: TOKENS.space.lg }}>
            <Text style={{ fontWeight: "bold", fontSize: TOKENS.font.lg }}>
              {statusName(group[0])}:
            </Text>
            <View style={styles.textSm}>
              {group[1].map((problem, index) => {
                counter++;
                return (
                  <View key={`p-${problem.answerId ?? index}`} style={[styles.borderedItem]} wrap={false}>
                    <View style={[styles.rowBetween, styles.wrap]}>
                      <View style={styles.w40}>
                        <Text>
                          {counter}. {problem.category}
                        </Text>
                      </View>
                      <View style={styles.w55}>
                        <Text>{problem?.description ?? ""}</Text>
                      </View>
                    </View>

                    <View style={[styles.rowBetween, styles.muted]}>
                      <Text>
                        Data rozwiązania {formatDateNumbers(problem?.resolvedDate)}
                      </Text>
                      <Text>
                        Zidentyfikowano {formatDateNumbers(problem?.identificationDate)}
                      </Text>
                      <Text>Przez {problem.personIdentifying ?? ""}</Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    );
  };

  const Participants = () => (
    <View>
      <Text style={styles.importantParagraph}>ZAKRES MONITOROWANYCH DANYCH</Text>
      <View>
        <View style={[styles.row, styles.bBottom, styles.bGray, styles.textSm]}>
          <Text style={styles.w20}>Nr uczestnika</Text>
          <Text style={styles.w20}>Nr zweryfikowanej wizyty</Text>
          <Text style={styles.w15}>100%</Text>
          <Text style={styles.w10}>RBV</Text>
          <Text style={styles.w35}>Komentarz</Text>
        </View>

        {participants.map((participant, index) => (
          <View key={`${participant.participantNumber}-${index}`} style={styles.borderedItemSolid}>
            <View style={[styles.row, styles.textSm]}>
              <Text style={styles.w20}>
                {index + 1}. {participant.participantNumber}
              </Text>
              <Text style={styles.w20}>{participant.visitNumber}</Text>
              <Text style={styles.w15}>{getBoolTextVal(participant.isFinshed)}</Text>
              <Text style={styles.w10}>{getBoolTextVal(participant.isVerified)}</Text>
              <Text style={styles.w35}>{participant.comment ?? ""}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View fixed>
          <ScientiaHeader />
        </View>

        {/* Title & Metadata */}
        <View>
          <Text style={styles.title}>
            {reportName}
            {reportType !== ReportType.CENTRAL_MONITORING ? ` w ${resortName}` : ""}
          </Text>

          {reportType === ReportType.CENTRAL_MONITORING &&
            cmResorts.length > 0 && (
              <View style={[styles.section, styles.textSm]}>
                <Text>
                  Zweryfikowano dane dla Ośrodków: {cmResorts.join("; ")}
                </Text>
              </View>
            )}

          {types.includes(TemplateType.METADATA) && (
            <View style={[styles.row, styles.wrap, styles.textSm, styles.gapMd, styles.muted]}>
              <Text>
                Numer Protokołu:{" "}
                <Text style={{ fontWeight: "bold", color: TOKENS.color.text }}>
                  {metadata.protocolNumber}{" "}
                </Text>
              </Text>
              <Text>
                Akronim Badania:{" "}
                <Text style={{ fontWeight: "bold", color: TOKENS.color.text }}>
                  {metadata.studyAcronym}
                </Text>
              </Text>
              <Text>
                Numer Ośrodka:{" "}
                <Text style={{ fontWeight: "bold", color: TOKENS.color.text }}>
                  {metadata.resortNumber}
                </Text>
              </Text>
              <Text>
                Główny badacz:{" "}
                <Text style={{ fontWeight: "bold", color: TOKENS.color.text }}>
                  {metadata.mainResearcher}
                </Text>
              </Text>
              {visitLabel ? (
                <Text>
                  Numer Wizyty:{" "}
                  <Text style={{ fontWeight: "bold", color: TOKENS.color.text }}>{visitLabel}</Text>
                </Text>
              ) : (
                <Text>
                  {metadata.visitNumber ? "Numer Wizyty: " : "Typ Wizyty: "}
                  <Text style={{ fontWeight: "bold", color: TOKENS.color.text }}>
                    {metadata.visitNumber != 0 && metadata.visitNumber != null
                      ? metadata.visitNumber
                      : metadata.visitType}
                  </Text>
                </Text>
              )}
              <Text>
                Data wizyty:{" "}
                <Text style={{ fontWeight: "bold", color: TOKENS.color.text }}>
                  {getVisitDate(metadata)}
                </Text>
              </Text>
              <Text>Adres: {address}</Text>
            </View>
          )}

          {types.includes(TemplateType.STATUS) && <MetaData />}

          <View style={styles.separator} />

          {types.includes(TemplateType.ISSUES) && (
            <Issues problemType={ProblemType.MAJOR} text="Lista poważnych znalezisk" />
          )}
          {types.includes(TemplateType.ISSUES) && (
            <Issues problemType={ProblemType.MINOR} text="Lista znalezisk" />
          )}
          {types.includes(TemplateType.PARTICIPANTS) && <Participants />}
        </View>

        {types.includes(TemplateType.ACTIONS) && (
          <ReportActionsTable items={actions} categories={categories} />
        )}

        {/* Questions */}
        {types.includes(TemplateType.QUESTIONS) && <QuestionsParagraph />}

        {/* Final comment */}
        {types.includes(TemplateType.FINALCOMMENT) && (
          <View>
            <Text>Komentarz końcowy:</Text>
            <Text>{metadata.finalComment}</Text>
          </View>
        )}

        {/* Signatures */}
        <View
          wrap={false}
          style={[styles.row, styles.gapLg, { marginRight: 20, marginTop: 40 }]}
        >
          <View style={[styles.flex1]}>
            <Text> </Text>
            <View style={[styles.bTop, styles.bBlack]}>
              <Text style={{ textAlign: "center", fontWeight: "bold" }}>
                DATA SPORZĄDZENIA:
              </Text>
              <Text style={{ textAlign: "center" }}>
                {new Date(composedDate).toLocaleDateString("pl-PL")}
              </Text>
            </View>
          </View>

          <View style={[styles.flex1]}>
            <Text> </Text>
            <View style={[styles.bTop, styles.bBlack]}>
              <Text style={{ textAlign: "center", fontWeight: "bold" }}>
                IMIĘ I NAZWISKO MONITORA (CRA):
              </Text>
              <Text style={{ textAlign: "center" }}>{capitalizeWords(author)}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        {types.includes(TemplateType.FOOTER) && (
          <View
            style={{
              position: "absolute",
              fontSize: TOKENS.font.base,
              bottom: 50,
              left: 0,
              right: 0,
              textAlign: "center",
              color: TOKENS.color.muted,
            }}
          >
            <Text>{footer}</Text>
          </View>
        )}

        {/* Page number */}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};
